import { GameServices } from "game/GameServices";
import { View } from "@westpierstudio/luma/mvc";
import { FullScreenPanel } from "./FullScreenPanel";
import { WheelPointer } from "./WheelPointer";
import { CashWedgeID, FreeSpinWedgeID, IsCacheWedgeID, IsFreeSpinsWedgeID, IsJackpotWedgeID, JackpotWedgeID, MultiplierWedgeID, OrderedWedgeData, WedgeID } from "servertypes/SharedTypes";
import { mod, toDeg, toRad } from "utils/Misc";
import { Signal } from "type-signals";
import { getEase, Tween } from "@westpierstudio/luma/createjs";
import { JackpotMeters } from "components/JackpotMeters";
import { minimumHeightSpecSelector, orientationSpecSelector } from "utils/Responsive";
import { Container, Sprite, AnimatedSprite, Circle, DisplayObject, InteractionEvent, Point, Text } from "pixi.js";
import { BoundedContainer } from "utils/BoundedContainer";

const MAX_INPUT_BUFFER = 5;

const WHEEL_RADIUS = 125;
const SEGMENT_COUNT = 16;
const SEG_ARC = 360/SEGMENT_COUNT;
const HALF_SEGMENT = SEG_ARC / 2;
const TWO_SEGMENTS = SEG_ARC * 2;

 //deg per sec
 const SPEED_1 = 360;
 const SPEED_2 = 120;
 const SPEED_3 = 10;
 const PHASE_3_MIN_DIST = 180;

enum PointerState {
    OFF,
    ON,
    FLASHING
}

enum SpinState {
    PHASE_1,
    PHASE_2,
    PHASE_3,
    TWEEN_END,
    STOP
}

class WheelSignals {
    wheelIntro = new Signal();
    wheelOutro = new Signal();
    animateInComplete = new Signal();
    spinStart = new Signal();
    spinComplete = new Signal();
    showWedgeWinComplete = new Signal();
    prizeSequenceComplete = new Signal();
    pointerCollision = new Signal();
    slideOffScreenComplete = new Signal();
}

export class Wheel extends View {
    private readonly logTime = this.services.resources.getSetting(`dev`).logWheelTime;
    private readonly spec: Record<string,any>;
    private readonly wheelPanel: FullScreenPanel;
    private readonly interactElementsContainer: Container;
    private readonly interactSprites: Sprite[] = [];
    private readonly pointer: WheelPointer;
    private readonly scaledWheelContainer: Container;
    private readonly rotationOffsetContainer: Container;
    private readonly wheelContainer: Container;
    private readonly multiplierWedgeContainer: Container;
    private readonly freeSpinsWedgeHeaders: {header: Text, headerBounds: BoundedContainer}[] = [];
    private readonly freeSpinWedges: Container[] = [];
    private readonly freeSpinTextContainer: Container;
    private readonly cashTextContainer: Container;
    private readonly jackpotTextContainer: Container;
    private readonly jackpotTextWedges: Sprite[] = [];
    private readonly wedgeGlow: Sprite;
    private readonly prizePopoutLayer: Container;
    private readonly jackpotMeters: JackpotMeters;
    private readonly hintText: Text;
    private readonly hintTextBounds: BoundedContainer;
    private readonly hintArrowsContainer: Container;
    private activeOrderedWedgeData?: OrderedWedgeData;
    private idleTweenObject = {};
    private wheelMoveTweenStartSpin?: createjs.Tween;
    private wheelScaleTweenReveal?: createjs.Tween;
    private wheelScaleTweenStartSpin?: createjs.Tween;
    private spinState = SpinState.STOP;
    private pointerState = PointerState.OFF;
    private wheelInputDistBuffer: number[] = [];
    private finalWheelAngles: number[] = [];
    private waitingForInputSpec!: any;
    private wheelSpinningSpec!: any;
    private waitingForInputScale!: any;
    private spinningScale!: any;
    private speed = 0;
    private acceleration = 0;
    private phase2Acceleration!: number;
    private phase3Acceleration = 0;
    private phase3Time = 0;
    private wheelBaseAngle = 0;
    private startAngle = 0;
    private finalAngle = 0;
    private distMoved = 0;
    private phase3Dist = 0;
    private startTime = 0;
    private haveSetup = false;
    private interacting = false;
    private waitingForInput = true;
    private spinClockwise = true;
    private winningWedgeID?: number;
    signals = new WheelSignals();
    prizeSheenLayer: Container;
    prizeSheenTop: AnimatedSprite;
    prizeSheenMiddle: AnimatedSprite;
    prizeSheenBottom: AnimatedSprite;

    constructor(protected override readonly services: GameServices) {
        super(services);

        const {factory, resources, layout} = this.services;

        this.spec = resources.getSpec(`wheel`);
        factory.applySpec(this, this.spec);

        this.wheelPanel = new FullScreenPanel(this.services, resources.getSetting(`wheel`).animatePanelTime);
        this.addChild(this.wheelPanel);

        this.wheelPanel.signals.animateInComplete.add(() =>  {
            this.signals.animateInComplete.dispatch();
            this.revealWheel();
        });

        this.wheelPanel.signals.animateOutComplete.add(() => {
            this.reset();
            this.signals.slideOffScreenComplete.dispatch();
        });

        this.scaledWheelContainer = factory.createContainer({name: `scaledWheelContainer`, ...this.spec.scaledWheelContainer}, this.wheelPanel.contentContainer);
        this.scaledWheelContainer.visible = false;

        const jackpotSpec = resources.getSpec(`wheeljackpotmeters`);

        this.jackpotMeters = this.wheelPanel.contentContainer.addChild(
            new JackpotMeters(this.services, jackpotSpec)
        );

        this.services.application.onTick.add(() => this.jackpotMeters.tick());

        this.interactElementsContainer = factory.createContainer({name: `interactElementsContainer`}, this.scaledWheelContainer);
        this.hintArrowsContainer = factory.createContainer({name: `hintArrowsContainer`}, this.interactElementsContainer);

        this.interactSprites = [];

        this.interactSprites.push(factory.createSprite(this.spec.slices.upArrow, this.hintArrowsContainer));
        this.interactSprites.push(factory.createSprite(this.spec.slices.downArrow, this.hintArrowsContainer));
        this.hintTextBounds = this.interactElementsContainer.addChild(new BoundedContainer({width: 88, height: Infinity}));
        this.hintText = factory.createText(resources.getMessage(`wheelInteract`), this.spec.interactText, this.hintTextBounds);

        this.interactSprites.push(this.hintText);

        this.rotationOffsetContainer = factory.createContainer({name: `rotationOffsetContainer`, angle: -90}, this.scaledWheelContainer);
        this.wheelContainer = factory.createContainer({name: `wheelContainer`}, this.rotationOffsetContainer);
        factory.createSprite({...this.spec.slices.wheelInner, angle: +90}, this.wheelContainer);
        this.multiplierWedgeContainer = factory.createContainer({name: `multiplierWedgeContainer`}, this.wheelContainer);
        this.cashTextContainer = factory.createContainer({name: `cashTextContainer`}, this.wheelContainer);
        this.jackpotTextContainer = factory.createContainer({name: `jackpotTextContainer`}, this.wheelContainer);
        this.freeSpinTextContainer = factory.createContainer({name: `freeSpinTextContainer`}, this.wheelContainer);

        const wheelOuterFrame = factory.createSprite(this.spec.slices.wheel, this.scaledWheelContainer);
        factory.applySpecAndParent(this.pointer = new WheelPointer(this.services), {}, this.scaledWheelContainer);
        this.wedgeGlow = factory.createSprite(this.spec.slices.wedgeGlow, this.scaledWheelContainer);
        this.prizePopoutLayer = factory.createContainer({name: `prizePopoutLayer`}, this.scaledWheelContainer);
        this.prizeSheenLayer = factory.createContainer({name: `prizeSheenLayer`}, this.scaledWheelContainer);

        this.prizeSheenTop = factory.createAnimatedSprite(this.spec.prizeSheenTop, this.prizeSheenLayer);
        this.prizeSheenMiddle = factory.createAnimatedSprite(this.spec.prizeSheenMiddle, this.prizeSheenLayer);
        this.prizeSheenBottom = factory.createAnimatedSprite(this.spec.prizeSheenBottom, this.prizeSheenLayer);
        this.prizeSheenTop.onComplete = () => this.prizeSheenTop.visible = false;
        this.prizeSheenMiddle.onComplete = () => this.prizeSheenMiddle.visible = false;
        this.prizeSheenBottom.onComplete = () => this.prizeSheenBottom.visible = false;

        this.wedgeGlow.alpha = 0;


        this.setupSpin();

        this.wheelContainer.hitArea = new Circle(0, 0, wheelOuterFrame.width / 2);

        this.rotationOffsetContainer.on(`pointerdown`, this.interactStart, this)
            .on(`pointermove`, this.interactMove, this)
            .on(`pointerup`, this.interactEnd, this)
            .on(`pointerupoutside`, this.interactEnd, this);

        this.visible = false;

        layout.onResize.add(this.resize, this);
        this.resize();
    }

    resize() {
        this.resizeJackpotMeters();
        this.resizeWheel();
        this.resizeHint();
    }

    resizeJackpotMeters() {
        const {factory, resources} = this.services;
        const jackpotMetersSpec = resources.getSpec(`wheeljackpotmeters`);
        const landscapeSpec = jackpotMetersSpec.landscape;
        const potraitSpec = minimumHeightSpecSelector(this.services, jackpotMetersSpec.portrait);
        factory.applySpec(this.jackpotMeters, orientationSpecSelector(this.services, landscapeSpec, potraitSpec));
    }

    resizeWheel() {
        const wheelStateSpecs = this.spec.scaledWheelContainer.states;
        const landscapeWaitingForInputSpec = wheelStateSpecs.awaitingInput.landscape;
        const portraitWaitingForInputSpec =  minimumHeightSpecSelector(this.services, wheelStateSpecs.awaitingInput.portrait);

        const landscapeWheelSpinningSpec = wheelStateSpecs.spinning.landscape;
        const portraitWheelSpinningSpec =  minimumHeightSpecSelector(this.services, wheelStateSpecs.spinning.portrait);

        this.waitingForInputSpec = orientationSpecSelector(this.services, landscapeWaitingForInputSpec, portraitWaitingForInputSpec);
        this.wheelSpinningSpec = orientationSpecSelector(this.services, landscapeWheelSpinningSpec, portraitWheelSpinningSpec);

        this.waitingForInputScale = this.waitingForInputSpec.scale;
        this.spinningScale = this.wheelSpinningSpec.scale;

        if (this.wheelScaleTweenReveal) {
            this.doWheelRevealTweens(this.wheelScaleTweenReveal.position);
        } else if (this.wheelScaleTweenStartSpin) {
            this.doWheelStartSpinTweens(this.wheelScaleTweenStartSpin.position);
        } else {
            this.setScaledWheelContainer(this.waitingForInput);
        }
    }

    resizeHint() {
        const {factory, resources, layout} = this.services;

        factory.applySpec(this.hintArrowsContainer, orientationSpecSelector(this.services, {visible: true}, {visible: false}));

        const landscapeSpecText = this.spec.interactText.landscape;
        const portraitSpecText = minimumHeightSpecSelector(this.services, this.spec.interactText.portrait);
        factory.applySpec(this.hintText, orientationSpecSelector(this.services, landscapeSpecText, portraitSpecText));

        const landscapeSpecBounds = this.spec.interactTextBounds.landscape;
        const portraitSpecBounds = minimumHeightSpecSelector(this.services, this.spec.interactTextBounds.portrait);
        const specBounds = orientationSpecSelector(this.services, landscapeSpecBounds, portraitSpecBounds);
        factory.applySpec(this.hintTextBounds, specBounds);
        this.hintTextBounds.maxSize = specBounds;
        this.hintTextBounds.update();

        this.hintText.text = resources.getMessage(layout.landscape ? `wheelInteract` : `wheelInteractSingleLine`);;
    }

    setJackpotvalues(major: number, minor: number, mini: number): void {
        this.jackpotMeters.setJackpotvalues(major, minor, mini);
    }

    setup(orderedWedgeData: OrderedWedgeData): void {

        if (!this.haveSetup) {
            this.activeOrderedWedgeData = orderedWedgeData;
            this.setupWedges(orderedWedgeData, WHEEL_RADIUS);
            this.haveSetup = true;
        }
    }

    updateCashPrizes(orderedWedgeData: OrderedWedgeData): void {

        this.activeOrderedWedgeData = orderedWedgeData;
        this.cashTextContainer.removeChildren();
        this.prizePopoutLayer.removeChildren();

        const {cashWedgeValue, wedgeRadialDistances} = this.spec;
        let angle;

        for (const [i, item] of orderedWedgeData.entries()) {

            angle = SEG_ARC * i;

            if (item.id in CashWedgeID) {
                this.setupCashWedgeText(item.id, item.coins, cashWedgeValue, wedgeRadialDistances.cashValue, angle, this.cashTextContainer);
            }
        }
    }

    reveal(isFreeSpins: boolean): void {
        this.configureFreeSpinsWedgeText(isFreeSpins);
        this.visible = true;
        this.wheelPanel.animateIn();
    }

    resumeInto(isFreeSpins: boolean): void {
        this.configureFreeSpinsWedgeText(isFreeSpins);
        this.visible = true;
        this.wheelPanel.setToDisplay();
        this.instantRevealWheel();
        this.resize();
    }

    spinToWinningWedge(winningWedgeIndex: number): void {

        this.pointerState = PointerState.ON;
        this.pointer.turnOn();

        this.doWheelStartSpinTweens();
        this.winningWedgeID = winningWedgeIndex;
        this.setFinalWheelAngle(winningWedgeIndex);
    }

    doWheelRevealTweens(tweenPosition = 0) {
        const {scaleUpTime1} = this.services.resources.getSetting(`wheel`);

        this.wheelScaleTweenReveal = Tween.get(this.scaledWheelContainer.scale, {override: true})
            .to(this.waitingForInputScale, scaleUpTime1)
            .call(() => {

                this.wheelBaseAngle = this.wheelContainer.angle = 0;
                this.rotationOffsetContainer.interactive = this.rotationOffsetContainer.buttonMode = true;
                this.wheelScaleTweenReveal = undefined;

                this.startIdle();
            });
        this.wheelScaleTweenReveal.setPosition(tweenPosition);
    }

    doWheelStartSpinTweens(tweenPosition = 0) {
        const {scaleUp2Delay, scaleUpTime2} = this.services.resources.getSetting(`wheel`);

        this.wheelMoveTweenStartSpin = Tween.get(this.scaledWheelContainer, {override: true})
            .wait(scaleUp2Delay)
            .to(this.wheelSpinningSpec, scaleUpTime2)
            .call(() => this.wheelMoveTweenStartSpin = undefined);

        this.wheelScaleTweenStartSpin = Tween.get(this.scaledWheelContainer.scale, {override: true})
            .wait(scaleUp2Delay)
            .to(this.spinningScale, scaleUpTime2)
            .call(() => {
                this.pointerState = PointerState.FLASHING;
                this.wheelScaleTweenStartSpin = undefined;
            });

        this.wheelMoveTweenStartSpin.setPosition(tweenPosition);
        this.wheelScaleTweenStartSpin.setPosition(tweenPosition);
    }

    /**
     *
     * @param dt Seconds
     */
    tick(dt: number): void {

        if (this.spinState === SpinState.STOP || this.spinState === SpinState.TWEEN_END) {
            return;
        }

        this.speed += (this.acceleration * dt);

        if (this.speed < 0) {
            this.speed = 0;
        }

        const distance = this.speed * dt;
        this.distMoved += distance;

        const newAngle = (this.spinClockwise) ? this.wheelContainer.angle + distance : this.wheelContainer.angle - distance;
        const lastAngle = this.wheelContainer.angle;
        this.wheelContainer.angle = mod(newAngle, 360);

        switch (this.spinState) {
            case SpinState.PHASE_1:

                if (this.distMoved >= 360 && this.winningWedgeID !== undefined) {

                    this.calculatePhase3(this.winningWedgeID);

                    this.distMoved = 0;
                    this.spinState = SpinState.PHASE_2;
                }

                break;
            case SpinState.PHASE_2:

                this.acceleration = this.phase2Acceleration;

                if (this.distMoved >= 360) {

                    this.distMoved = 0;
                    this.spinState = SpinState.PHASE_3;
                }

                break;
            case SpinState.PHASE_3:

                this.spinState = SpinState.PHASE_3;

                this.acceleration = this.phase3Acceleration;

                if (this.distMoved >= this.phase3Dist || this.speed === 0) {

                    this.spinState = SpinState.TWEEN_END;
                    this.startEndTween();
                }

                break;
        }

        this.updatePointer(lastAngle, this.wheelContainer.angle);
    }

    showWedgeWin(winningWedge: WedgeID): void {

        this.pointer.setBrightness(IsJackpotWedgeID(winningWedge) ? 1 : 0.5);
        this.pointerState = PointerState.ON;

        const {wedgeGlowFadeTime, wedgeGlowHoldTime} = this.services.resources.getSetting(`wheel`);

        Tween.get(this.wedgeGlow)
        .to({alpha: 1.0}, wedgeGlowFadeTime)
        .wait(wedgeGlowHoldTime)
        .to({alpha: 0}, wedgeGlowFadeTime)
        .call(() => this.signals.showWedgeWinComplete.dispatch());

        if (IsCacheWedgeID(winningWedge) || IsJackpotWedgeID(winningWedge) || IsFreeSpinsWedgeID(winningWedge)) {
            this.playPrizeBounce(winningWedge);
            this.playSheen();
        }
    }

    playPrizeBounce(winningWedge: CashWedgeID | JackpotWedgeID | FreeSpinWedgeID) {
        const {prizeSequence} = this.services.resources.getSetting(`wheel`);
        const {scale1, scale2, scale3, scale4, finalScaleDelay} = prizeSequence;

        const wedgeWithMatchingID = (wedge: DisplayObject) => wedge.name === winningWedge;

        const wedge =
            this.jackpotTextContainer.children.find(wedgeWithMatchingID) ||
            this.freeSpinTextContainer.children.find(wedgeWithMatchingID) ||
            this.cashTextContainer.children.find(wedgeWithMatchingID);

        if (wedge) {
            this.movePrizeToPopoutLayer(wedge);

            Tween.get(wedge.scale)
            .to({x: scale1.value, y: scale1.value}, scale1.time, getEase(scale1.ease))
            .to({x: scale2.value, y: scale2.value}, scale2.time, getEase(scale2.ease))
            .to({x: scale3.value, y: scale3.value}, scale3.time, getEase(scale3.ease))
            .wait(finalScaleDelay)
            .to({x: scale4.value, y: scale4.value}, scale4.time, getEase(scale4.ease));
        } else {
            throw new Error(`Attempted to play prize bounce, but no prize wedge found with name matching id: "${winningWedge}"`);
        }

    }

    playSheen() {
        const {prizeSheen} = this.services.resources.getSetting(`wheel`);
        const overlapFrames = prizeSheen.overlapFrames;
        const frameRate = this.spec.prizeSheenTop.fps ?? 30;
        const overlapTime = 1000 / frameRate * overlapFrames;

        Tween.get(this.prizeSheenTop)
            .call(()=> this.prizeSheenTop.visible = true)
            .call(()=> this.prizeSheenTop.gotoAndPlay(0))
            .wait(overlapTime)
            .call(()=> this.prizeSheenMiddle.visible = true)
            .call(()=> this.prizeSheenMiddle.gotoAndPlay(0))
            .wait(overlapTime)
            .call(()=> this.prizeSheenBottom.visible = true)
            .call(()=> this.prizeSheenBottom.gotoAndPlay(0));
    }

    movePrizeToPopoutLayer(wedge: DisplayObject) {
        // match the rotations so you can directly move between the containers
        this.prizePopoutLayer.rotation = this.wheelContainer.rotation + this.rotationOffsetContainer.rotation;
        wedge.parent.removeChild(wedge);
        this.prizePopoutLayer.addChild(wedge);
    }

    slideOffScreen(): void {

        this.wheelPanel.animateOut();
        this.signals.wheelOutro.dispatch();
    }

    skipSpin(): void {
        Tween.removeTweens(this.wheelContainer);
        this.wheelContainer.angle = this.finalAngle;
        this.setScaledWheelContainer(false);
        this.spinState = SpinState.STOP;
        this.pointerState = PointerState.FLASHING;
        this.pointer.setBrightness(0.5);
        this.signals.spinComplete.dispatch();
    }

    override reset(): void {

        this.visible = false;

        this.wheelPanel.reset();

        Tween.removeTweens(this.interactElementsContainer);
        this.interactElementsContainer.alpha = 1.0;
        this.setInteractSpritesTint();

        this.pointer.reset();

        this.scaledWheelContainer.visible = false;
        this.setScaledWheelContainer(true);

        Tween.removeTweens(this.wheelContainer);
        this.wheelContainer.angle = 0;

        Tween.removeTweens(this.wedgeGlow);
        this.wedgeGlow.alpha = 0;

        this.jackpotMeters.reset();

        Tween.removeTweens(this.idleTweenObject);

        if (this.jackpotTextWedges.length > 0) {

            for (const wedge of this.jackpotTextWedges) {

                Tween.removeTweens(wedge.scale);
                wedge.scale.set(1.0);
            }

            this.jackpotTextContainer.addChild(...this.jackpotTextWedges);
        }

        if (this.freeSpinWedges.length > 0) {

            for (const wedge of this.freeSpinWedges) {

                Tween.removeTweens(wedge.scale);
                wedge.scale.set(1.0);
            }

            this.freeSpinTextContainer.addChild(...this.freeSpinWedges);
        }

        this.prizeSheenTop.gotoAndStop(0);
        this.prizeSheenMiddle.gotoAndStop(0);
        this.prizeSheenBottom.gotoAndStop(0);
        this.prizeSheenTop.visible = false;
        this.prizeSheenMiddle.visible = false;
        this.prizeSheenBottom.visible = false;
        Tween.removeTweens(this.prizeSheenTop);

        if (this.activeOrderedWedgeData) {
            this.updateCashPrizes(this.activeOrderedWedgeData);
        }

        this.wheelMoveTweenStartSpin = undefined;
        this.wheelScaleTweenReveal = undefined;
        this.wheelScaleTweenStartSpin = undefined;

        this.spinState = SpinState.STOP;
        this.pointerState = PointerState.OFF;

        this.wheelInputDistBuffer = [];

        this.speed = 0;
        this.acceleration = 0;
        this.phase3Acceleration = 0;
        this.phase3Time = 0;
        this.wheelBaseAngle = 0;
        this.startAngle = 0;
        this.finalAngle = 0;
        this.distMoved = 0;
        this.phase3Dist = 0;
        this.startTime = 0;

        this.interacting = false;
        this.waitingForInput = true;
        this.spinClockwise = true;
        this.winningWedgeID = undefined;

        this.resize();
    }

    private setupWedges(orderedWedgeData: OrderedWedgeData, radius: number): void {

        const {cashWedgeValue, wedgeRadialDistances} = this.spec;

        let angle;

        for (const [i, item] of orderedWedgeData.entries()) {

            angle = SEG_ARC * i;

            if (item.id in CashWedgeID) {

                this.setupCashWedgeText(item.id, item.coins, cashWedgeValue, wedgeRadialDistances.cashValue, angle, this.cashTextContainer);

            } else if (item.id in FreeSpinWedgeID) {

                this.setupFreeSpinsWedgeText(item.id, item.freeSpins as number, angle);

            } else if (item.id in JackpotWedgeID) {

                switch(item.id) {
                    case JackpotWedgeID.JACKPOT_0:
                        this.setupJackpotWedgeText(item.id, `jackpotText0`, wedgeRadialDistances.jackpotText0, angle, this.jackpotTextContainer);
                        break;
                    case JackpotWedgeID.JACKPOT_1:
                        this.setupJackpotWedgeText(item.id, `jackpotText1`, wedgeRadialDistances.jackpotText1, angle, this.jackpotTextContainer);
                        break;
                    case JackpotWedgeID.JACKPOT_2:
                        this.setupJackpotWedgeText(item.id, `jackpotText2`, wedgeRadialDistances.jackpotText2, angle, this.jackpotTextContainer);
                        break;
                    default:
                        break;
                }
            } else if (item.id in MultiplierWedgeID) {

                switch(item.id) {
                    case MultiplierWedgeID.MULTIPLIERS_0:
                        this.setupWedge(`multiWedge0`, wedgeRadialDistances.multiplier0, angle, this.multiplierWedgeContainer);
                        break;
                    case MultiplierWedgeID.MULTIPLIERS_1:
                        this.setupWedge(`multiWedge1`, wedgeRadialDistances.multiplier1, angle, this.multiplierWedgeContainer);
                        break;
                    case MultiplierWedgeID.MULTIPLIERS_2:
                        this.setupWedge(`multiWedge2`, wedgeRadialDistances.multiplier2, angle, this.multiplierWedgeContainer);
                        break;
                    case MultiplierWedgeID.MULTIPLIERS_3:
                        this.setupWedge(`multiWedge3`, wedgeRadialDistances.multiplier3, angle, this.multiplierWedgeContainer);
                        break;
                    default:
                        break;
                }
            }

            this.finalWheelAngles.push(360 - (i * SEG_ARC));
        }
    }

    private setupFreeSpinsWedgeText(id: WedgeID, freeSpins: number, angle: number) {

        const {factory} = this.services;
        const {freeSpinsWedgeHeader, freeSpinsWedgeValue, freeSpinsWedgeHeaderBounds} = this.spec;

        const container = factory.createContainer({name: id}, this.freeSpinTextContainer);

        const headerBounds = container.addChild(new BoundedContainer(freeSpinsWedgeHeaderBounds));
        const header = this.services.factory.createText(
            this.services.resources.getMessage(`freeSpinsWedgeHeader`),
            freeSpinsWedgeHeader,
            headerBounds
        );

        this.services.factory.createText(
            freeSpins.toString(),
            {...freeSpinsWedgeValue, y: header.height},
            container
        );

        this.freeSpinsWedgeHeaders.push({header, headerBounds});
        this.freeSpinWedges.push(container);

        container.x = this.spec.wedgeRadialDistances.freeSpins * Math.cos(toRad(angle));
        container.y = this.spec.wedgeRadialDistances.freeSpins * Math.sin(toRad(angle));
        container.angle = 90 + angle;
    }

    private setupWedge(textureKey: string, radius: number, angle: number, container: Container): void {

        this.services.factory.createSpriteFromTextureKey(
            textureKey,
            {
                x: radius * Math.cos(toRad(angle)),
                y: radius * Math.sin(toRad(angle)),
                anchor: {x: 0.5, y: 0},
                angle: 90 + angle
            },
            container
        );
    }

    private setupJackpotWedgeText(id: WedgeID, texture: string, radius: number, angle: number, container: Container) {
        this.jackpotTextWedges.push(this.services.factory.createSprite({
            _key: texture,
            name: id,
            x: radius * Math.cos(toRad(angle)),
            y: radius * Math.sin(toRad(angle)),
            anchor: 0.5,
            angle: 90 + angle
        }, container));
    }

    private setupCashWedgeText(id: WedgeID, value: number, spec: any, radius: number, angle: number, container: Container): void {

        const prizeString = this.services.currency.formatCoins(value, {group: false, addTrailingZeros: false, useSubUnits: false, symbol: ``});

        const verticalSpacingMap = this.spec.cashValueVerticalCharacterSpacing;
        const verticalPostSpacingMap = this.spec.cashValueVerticalPostCharacterSpacing;

        const valueContainer = this.services.factory.createContainer({name: id}, container);
        const bounds = new BoundedContainer(this.spec.cashWedgeBounds);
        valueContainer.addChild(bounds);

        let radiusOffset = 0;
        let charIndex = 0;

        for (const char of prizeString) {
            const prizeChar = this.services.factory.createText(char, spec, bounds);
            prizeChar.y = radiusOffset;

            const postCharacterOffset = verticalPostSpacingMap[char] ?? 0;
            radiusOffset += postCharacterOffset;

            const nextChar = prizeString[charIndex + 1];
            if (nextChar) {
                const verticalSpacing = verticalSpacingMap[nextChar] ?? verticalSpacingMap[`default`];
                radiusOffset += verticalSpacing;
            }
            charIndex++;
        }

        bounds.update();
        valueContainer.angle = 90 + angle;
        valueContainer.x = radius * Math.cos(toRad(angle));
        valueContainer.y = radius * Math.sin(toRad(angle));
    }

    private setupSpin(): void {

        const phase2Time = (2 * SPEED_1) / (SPEED_1 + SPEED_2);
        this.phase2Acceleration = (SPEED_2 - SPEED_1) / phase2Time;
    }

    private instantRevealWheel(): void {
        this.waitingForInput = true;
        this.scaledWheelContainer.scale.set(0.5);
        this.scaledWheelContainer.visible = true;

        this.interactElementsContainer.alpha = 1.0;

        this.wheelBaseAngle = this.wheelContainer.angle = 0;
        this.rotationOffsetContainer.interactive = this.rotationOffsetContainer.buttonMode = true;

        this.winningWedgeID = undefined;

        this.startIdle();
    }

    private revealWheel(): void {
        this.waitingForInput = true;
        this.scaledWheelContainer.scale.x = 0;
        this.scaledWheelContainer.scale.y = 0;
        this.scaledWheelContainer.visible = true;

        this.interactElementsContainer.alpha = 1.0;

        this.winningWedgeID = undefined;

        this.doWheelRevealTweens();
        this.signals.wheelIntro.dispatch();
    }

    private interactStart(event: InteractionEvent): void {

        this.wheelInputDistBuffer = [];
        this.startAngle = this.inputEventToWheelAngle(event.data.global);

        this.interacting = true;

        this.stopIdle();
    }

    private inputEventToWheelAngle(point: Point): number {

        const {x, y} = point;
        const localPos = this.toLocal({x, y});

        let ang = toDeg(Math.atan2(localPos.x - this.scaledWheelContainer.x, localPos.y - this.scaledWheelContainer.y));

        //correct to be continous
        if (ang < 0) {
            ang += 360;
        }

        //Match setup of wheel view, with 0 rotation at the top running clockwise to 360
        ang -= 180;
        ang = mod(360-ang, 360);

        return ang;
    }

    private interactMove(event: any): void {

        if (this.interacting) {

            const angle = this.inputEventToWheelAngle(event.data.global);
            const lastAngle = this.wheelContainer.angle;


            const distance = angle - this.startAngle;
            const currentAngle = mod(this.wheelBaseAngle + distance, 360);
            this.wheelContainer.angle = currentAngle;
            this.addToInputBuffer(distance);

            this.updatePointer(lastAngle, currentAngle);
        }
    }

    private interactEnd(_event: InteractionEvent): void {

        this.rotationOffsetContainer.interactive = this.rotationOffsetContainer.buttonMode = false;
        this.interacting = false;

        if (this.wheelInputDistBuffer.length > 0) {

            const lastIndex = this.wheelInputDistBuffer.length - 1;
            const change = this.wheelInputDistBuffer[lastIndex] - this.wheelInputDistBuffer[0];

            this.spinClockwise = (change > 0) ? true : false;
        } else {
            this.spinClockwise = Math.random() >= 0.5;
        }

        if (this.logTime) {
            this.startTime = Date.now();
        }

        this.waitingForInput = false;
        this.startSpin();
        this.signals.spinStart.dispatch();
    }

    private addToInputBuffer(dist: number): void {

        this.wheelInputDistBuffer.push(dist);

        if (this.wheelInputDistBuffer.length > MAX_INPUT_BUFFER) {
            this.wheelInputDistBuffer.splice(0, 1);
        }
    }

    private startSpin(): void {
        this.speed = SPEED_1;
        this.acceleration = 0;
        this.distMoved = 0;
        this.spinState = SpinState.PHASE_1;

        const {scaleUp2Delay} = this.services.resources.getSetting(`wheel`);

        Tween.get(this.interactElementsContainer, {override: true})
        .to({alpha: 0}, scaleUp2Delay);
    }

    private calculatePhase3(winningWedgeIndex: number): void {
        let wedgeAngleToOrigin, wheelAngleToOrigin;

        if (this.spinClockwise) {
            wheelAngleToOrigin = 360 - this.wheelContainer.angle;
            wedgeAngleToOrigin = 360 - (SEG_ARC * winningWedgeIndex);
        } else {
            wheelAngleToOrigin = this.wheelContainer.angle;
            wedgeAngleToOrigin = SEG_ARC * winningWedgeIndex;
        }

        this.phase3Dist = wheelAngleToOrigin + wedgeAngleToOrigin - SEG_ARC/2;

        while (this.phase3Dist < PHASE_3_MIN_DIST) {
            this.phase3Dist += 360;
        }

        this.phase3Time = (2 * this.phase3Dist) / (SPEED_2 + SPEED_3);
        this.phase3Acceleration = (SPEED_3 - SPEED_2) / this.phase3Time;
    }

    private setFinalWheelAngle(winningWedgeIndex: number): void {

        this.finalAngle = this.finalWheelAngles[winningWedgeIndex];

        if (this.finalAngle === 360 && this.spinClockwise === false) {
            this.finalAngle = 0;
        }
    }

    private updatePointer(lastAngle: number, currentAngle: number): void {

        // offset the angles by half a segment so that the segment boundarys are at the edge, not center of segments
        const offsetLastAngle = lastAngle + HALF_SEGMENT;
        const offsetCurrentAngle = currentAngle + HALF_SEGMENT;

        // pointer brightness is based on the fraction through the segment, so when the wheel stops, with the pointer halfway, the brightness is 0.5;
        if (this.pointerState === PointerState.FLASHING) {
            const clockwise = lastAngle > currentAngle;
            const segmentFractionCovered = mod(offsetCurrentAngle, SEG_ARC) / SEG_ARC;
            const pointerBrightness = clockwise ? segmentFractionCovered : 1 - segmentFractionCovered;
            this.pointer.setBrightness(pointerBrightness);
        }

        // get the angle relative to a pair of segments
        const normalisedLast = mod(offsetLastAngle, TWO_SEGMENTS);
        const normalisedCurrent = mod(offsetCurrentAngle, TWO_SEGMENTS);

        // if there is an angle on either side of the midpoint between 2 segments, you crossed the segment boundary.
        const collision = (normalisedLast < SEG_ARC && normalisedCurrent > SEG_ARC) || (normalisedLast > SEG_ARC && normalisedCurrent < SEG_ARC);
        if (collision) {
            this.signals.pointerCollision.dispatch();
            return;
        }

        // if we didn't get an accurate collision - but the angle changed by more then a segment width, we must have crossed a segment boundary.
        const sweptDistance = Math.abs(currentAngle - lastAngle);
        if (sweptDistance > SEG_ARC && sweptDistance <= (360 - SEG_ARC)) { // (when the angle wraps at 0/360, it can trigger a false collision), so just ignore around that area. if the wheel is doing 1 rotation per frame, you got bigger problems
            this.signals.pointerCollision.dispatch();
        }
    }


    private startEndTween():  void {

        const {endPhaseTween} = this.services.resources.getSetting(`wheel`);

        const timeMS = 1000 * Math.abs(this.finalAngle - this.wheelContainer.angle)/SPEED_3;
        let lastAngle = this.wheelContainer.angle;

        Tween.get(this.wheelContainer)
        .to({angle: this.finalAngle}, timeMS, getEase(endPhaseTween))
        .call(() => {

            if (this.logTime) {
                console.log(`Wheel Time(ms): `, Date.now() - this.startTime);
            }

            this.spinState = SpinState.STOP;
            this.signals.spinComplete.dispatch();
        }).addEventListener(`change`, () => {
            this.updatePointer(lastAngle, this.wheelContainer.angle);
            lastAngle = this.wheelContainer.angle;
        });
    }
    private setScaledWheelContainer(awaitingInput = false): void {

        const {factory} = this.services;

        Tween.removeTweens(this.scaledWheelContainer.pivot);
        Tween.removeTweens(this.scaledWheelContainer);
        Tween.removeTweens(this.scaledWheelContainer.scale);

        this.wheelMoveTweenStartSpin = undefined;

        if (awaitingInput) {
            factory.applySpec(this.scaledWheelContainer, this.waitingForInputSpec);
        } else {
            factory.applySpec(this.scaledWheelContainer, this.wheelSpinningSpec);
        }
    }

    private configureFreeSpinsWedgeText(isFreeSpins: boolean) {
        const {resources} = this.services;
        const message = resources.getMessage(isFreeSpins ? `extraFreeSpinsWedgeHeader`: `freeSpinsWedgeHeader`);
        const spec = isFreeSpins ? this.spec.extraFreeSpinsWedgeHeader : this.spec.freeSpinsWedgeHeader;

        for (const {header, headerBounds} of this.freeSpinsWedgeHeaders) {
            header.text = message;
            header.style = spec.style;
            headerBounds.update();
        }
    }

    private startIdle(): void {

        const {wait} = this.services.resources.getSetting(`wheel`).idle;

        Tween.get(this.idleTweenObject)
        .wait(wait)
        .call(() => {
            this.interactSpritesPulse();
        });
    }

    private stopIdle(): void {
        Tween.removeTweens(this.idleTweenObject);
    }

    private interactSpritesPulse(): void {

        const {onTime, offTime, interactSpriteTint} = this.services.resources.getSetting(`wheel`).idle;

        Tween.get(this.idleTweenObject)
        .wait(onTime)
        .call(() => {
            this.setInteractSpritesTint(interactSpriteTint);
        })
        .wait(offTime)
        .call(() => {

            this.setInteractSpritesTint();
            this.interactSpritesPulse();
        });
    }

    private setInteractSpritesTint(tint=0xFFFFFF): void {

        for (const item of this.interactSprites) {
            item.tint = tint;
        }
    }
}